import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Pencil, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import { num } from "@/lib/format";

const triggerCls =
  "flex h-8 w-full items-center justify-between gap-1 rounded-sm border border-slate-300 bg-white px-2 text-left text-[12.5px] text-slate-800 outline-none transition-colors duration-150 hover:border-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400";

const Combo = ({
  label,
  value,
  options,
  onSelect,
  onClear,
  disabled,
  disabledHint,
  placeholder,
  loading,
  testid,
  recent = [],
  countLabel = "in NL geregistreerd",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) =>
      `${o.name} ${o.line || ""} ${o.group || ""}`.toLowerCase().includes(q)
    );
  }, [options, query]);

  return (
    <div>
      <label className="label-xs">{label}</label>
      <div className="relative mt-1">
        <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(""); }}>
          <PopoverTrigger asChild>
            <button type="button" disabled={disabled} data-testid={testid} className={triggerCls}>
              <span className={`truncate ${value ? "font-semibold" : "text-slate-400"}`}>
                {value || (disabled ? disabledHint : placeholder)}
              </span>
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[min(22rem,90vw)] border-slate-300 p-0"
            data-testid={`${testid}-dropdown`}
          >
            <Command shouldFilter={false} loop>
              <CommandInput
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder={`Zoek ${label.toLowerCase()}…`}
                data-testid={`${testid}-search`}
                className="text-[12.5px]"
              />
              <CommandList className="max-h-64">
                <CommandEmpty className="px-3 py-4 text-[12px] text-slate-500">
                  Geen resultaten in de catalogus.
                </CommandEmpty>
                {recent.length > 0 && !query && (
                  <CommandGroup heading="Veel gebruikt">
                    {recent.map((r) => (
                      <CommandItem
                        key={`recent-${r.label || r.name}`}
                        value={`recent-${r.label || r.name}`}
                        onSelect={() => {
                          onSelect(r);
                          setOpen(false);
                          setQuery("");
                        }}
                        data-testid={`${testid}-recent-${(r.label || r.name || "").replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-[12.5px]"
                      >
                        {r.label || r.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                <CommandGroup heading={`${filtered.length} opties`}>
                  {filtered.slice(0, 400).map((o) => (
                    <CommandItem
                      key={o.key || o.name}
                      value={o.key || o.name}
                      onSelect={() => {
                        onSelect(o);
                        setOpen(false);
                        setQuery("");
                      }}
                      data-testid={`${testid}-option-${(o.name || "").replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-[12.5px]"
                    >
                      <Check
                        className={`mr-1.5 h-3.5 w-3.5 ${
                          value === o.name ? "text-blue-600" : "text-transparent"
                        }`}
                      />
                      <span className="flex-1 truncate">
                        {o.name}
                        {(o.line || o.group) && (
                          <span className="ml-1.5 text-[10.5px] text-slate-400">
                            {o.line || o.group}
                          </span>
                        )}
                      </span>
                      {o.count ? (
                        <span
                          className="ml-2 shrink-0 text-[10px] text-slate-400"
                          title={countLabel}
                        >
                          {num(o.count)}
                        </span>
                      ) : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {value && !disabled && (
          <button
            type="button"
            onClick={onClear}
            title="Selectie wissen"
            data-testid={`${testid}-clear`}
            className="absolute right-6 top-1.5 rounded-sm p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export const VehicleSelector = ({ selection, onChange, testid = "vehicle-selector" }) => {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [variants, setVariants] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState({});
  const [manual, setManual] = useState(false);
  const requested = useRef({});

  const busy = (key, on) => setLoading((s) => ({ ...s, [key]: on }));

  useEffect(() => {
    busy("makes", true);
    api
      .get("/taxonomy/makes")
      .then((r) => setMakes(r.data.makes || []))
      .catch(() => {})
      .finally(() => busy("makes", false));
    api
      .get("/taxonomy/recent")
      .then((r) => setRecent(r.data.recent || []))
      .catch(() => {});
  }, []);

  const loadModels = useCallback(async (make) => {
    if (!make) return setModels([]);
    busy("models", true);
    try {
      const { data } = await api.get(`/taxonomy/models?make=${encodeURIComponent(make)}`);
      setModels(data.models || []);
    } catch {
      setModels([]);
    }
    busy("models", false);
  }, []);

  const loadGenerationsAndVariants = useCallback(async (make, model, generation) => {
    if (!make || !model) {
      setGenerations([]);
      setVariants([]);
      return;
    }
    busy("generations", true);
    busy("variants", true);
    const base = `make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`;
    api
      .get(`/taxonomy/generations?${base}`)
      .then((r) => setGenerations(r.data.generations || []))
      .catch(() => setGenerations([]))
      .finally(() => busy("generations", false));
    api
      .get(`/taxonomy/variants?${base}${generation ? `&generation=${encodeURIComponent(generation)}` : ""}`)
      .then((r) => setVariants(r.data.variants || []))
      .catch(() => setVariants([]))
      .finally(() => busy("variants", false));
  }, []);

  // hydrate lists for a preselected / restored selection (search profile)
  useEffect(() => {
    if (selection.make && requested.current.make !== selection.make) {
      requested.current.make = selection.make;
      loadModels(selection.make);
    }
    const key = `${selection.make}|${selection.model}|${selection.generation || ""}`;
    if (selection.make && selection.model && requested.current.model !== key) {
      requested.current.model = key;
      loadGenerationsAndVariants(selection.make, selection.model, selection.generation);
    }
  }, [selection.make, selection.model, selection.generation, loadModels, loadGenerationsAndVariants]);

  const pickMake = (option) => {
    if (option.label) {
      onChange({
        make: option.make,
        model: option.model || "",
        generation: option.generation || "",
        variant: option.variant || "",
        make_id: null,
        model_id: null,
      });
      return;
    }
    onChange({
      make: option.name,
      model: "",
      generation: "",
      variant: "",
      make_id: option.source_ids?.autoscout24?.make_id ?? null,
      model_id: null,
    });
  };

  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        <Combo
          label="Merk"
          testid="select-make"
          value={selection.make}
          options={makes}
          loading={loading.makes}
          placeholder="Kies merk"
          recent={recent}
          onSelect={pickMake}
          onClear={() =>
            onChange({ make: "", model: "", generation: "", variant: "", make_id: null, model_id: null })
          }
        />
        <Combo
          label="Model"
          testid="select-model"
          value={selection.model}
          options={models}
          loading={loading.models}
          disabled={!selection.make}
          disabledHint="Selecteer eerst een merk"
          placeholder="Alle modellen"
          onSelect={(o) =>
            onChange({
              model: o.name,
              generation: "",
              variant: "",
              model_id: o.source_ids?.autoscout24?.model_id ?? null,
            })
          }
          onClear={() => onChange({ model: "", generation: "", variant: "", model_id: null })}
        />
        <Combo
          label="Generatie"
          testid="select-generation"
          value={selection.generation}
          options={generations}
          loading={loading.generations}
          disabled={!selection.model}
          disabledHint="Selecteer eerst een model"
          placeholder="Alle generaties"
          onSelect={(o) => onChange({ generation: o.name, variant: "" })}
          onClear={() => onChange({ generation: "", variant: "" })}
        />
        <Combo
          label="Uitvoering / type"
          testid="select-variant"
          value={selection.variant}
          options={variants}
          loading={loading.variants}
          disabled={!selection.model}
          disabledHint="Selecteer eerst een model"
          placeholder="Alle uitvoeringen"
          onSelect={(o) => onChange({ variant: o.name })}
          onClear={() => onChange({ variant: "" })}
        />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setManual((m) => !m)}
          data-testid="toggle-manual-vehicle"
          className="flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:underline"
        >
          <Pencil className="h-3 w-3" /> Model niet gevonden?
        </button>
        {!selection.model && selection.make && (
          <span className="text-[11px] text-slate-500">
            Alleen merk gekozen — de scan pakt alle modellen van {selection.make}.
          </span>
        )}
        {selection.manual_entry?.length > 0 && (
          <span className="chip border-amber-300 bg-amber-50 text-amber-800" data-testid="manual-entry-badge">
            Handmatige invoer: {selection.manual_entry.join(", ")}
          </span>
        )}
      </div>

      {manual && (
        <div className="mt-2 grid grid-cols-1 gap-2.5 rounded-sm border border-dashed border-slate-300 bg-slate-50 p-2.5 sm:grid-cols-3">
          {[
            ["model", "Model (handmatig)"],
            ["generation", "Generatie (handmatig)"],
            ["variant", "Uitvoering (handmatig)"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="label-xs">{label}</label>
              <input
                value={selection[key] || ""}
                onChange={(e) =>
                  onChange({ [key]: e.target.value, ...(key === "model" ? { model_id: null } : {}) })
                }
                data-testid={`manual-${key}`}
                className="mt-1 h-8 w-full rounded-sm border border-slate-300 bg-white px-2 text-[12.5px] outline-none focus:border-blue-500"
                placeholder={key === "generation" ? "bijv. 992" : ""}
              />
            </div>
          ))}
          <p className="col-span-full text-[10.5px] text-slate-500">
            Handmatige invoer wordt gelabeld als HANDMATIGE INVOER en niet automatisch aan
            marktplaats-ID's gekoppeld — de zoekopdracht valt dan terug op tekstmatching.
          </p>
        </div>
      )}
    </div>
  );
};
